import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, Clock, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

// Notification messages
const REMINDER_MESSAGES = [
  "¿Todo bien? 💫",
  "¿Cómo te sientes ahora? 🌟",
  "Momento de check-in emocional ✨",
  "¿Cómo va tu día? 💭",
  "Recuerda registrar tu estado de ánimo 🎯",
];

// Get random message
const getRandomMessage = () => {
  return REMINDER_MESSAGES[Math.floor(Math.random() * REMINDER_MESSAGES.length)];
};

// Check if notifications are supported
const isNotificationSupported = () => {
  return "Notification" in window;
};

// Request notification permission
const requestPermission = async () => {
  if (!isNotificationSupported()) {
    return "unsupported";
  }
  
  if (Notification.permission === "granted") {
    return "granted";
  }
  
  if (Notification.permission === "denied") {
    return "denied";
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

// Send notification
const sendNotification = (title, body, onClick) => {
  if (Notification.permission !== "granted") return;
  
  const notification = new Notification(title, {
    body,
    icon: "/favicon.ico",
    badge: "/favicon.ico",
    tag: "moodpunk-reminder",
    renotify: true,
    requireInteraction: false,
  });
  
  notification.onclick = () => {
    window.focus();
    notification.close();
    if (onClick) onClick();
  };
  
  // Auto close after 10 seconds
  setTimeout(() => notification.close(), 10000);
};

// Reminder intervals in minutes
const INTERVALS = [
  { value: 30, label: "30 minutos" },
  { value: 60, label: "1 hora" },
  { value: 120, label: "2 horas" },
  { value: 240, label: "4 horas" },
  { value: 480, label: "8 horas" },
];

export const NotificationSettings = ({ onClose }) => {
  const [permission, setPermission] = useState(Notification.permission);
  const [enabled, setEnabled] = useState(false);
  const [interval, setInterval] = useState(60);
  const [loading, setLoading] = useState(false);

  // Load settings from localStorage
  useEffect(() => {
    const settings = localStorage.getItem("moodpunk_notifications");
    if (settings) {
      const parsed = JSON.parse(settings);
      setEnabled(parsed.enabled);
      setInterval(parsed.interval || 60);
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = (newEnabled, newInterval) => {
    localStorage.setItem("moodpunk_notifications", JSON.stringify({
      enabled: newEnabled,
      interval: newInterval,
    }));
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    const result = await requestPermission();
    setPermission(result);
    
    if (result === "granted") {
      setEnabled(true);
      saveSettings(true, interval);
      toast.success("¡Notificaciones activadas!");
      
      // Send test notification
      setTimeout(() => {
        sendNotification("MoodPunk", "¡Notificaciones activadas! Te recordaremos registrar tu estado de ánimo.");
      }, 1000);
    } else if (result === "denied") {
      toast.error("Permiso de notificaciones denegado. Actívalas en la configuración de tu navegador.");
    }
    setLoading(false);
  };

  const handleDisableNotifications = () => {
    setEnabled(false);
    saveSettings(false, interval);
    toast.success("Notificaciones desactivadas");
  };

  const handleIntervalChange = (newInterval) => {
    setInterval(newInterval);
    saveSettings(enabled, newInterval);
    if (enabled) {
      toast.success(`Recordatorios cada ${INTERVALS.find(i => i.value === newInterval)?.label}`);
    }
  };

  const handleTestNotification = () => {
    if (Notification.permission === "granted") {
      sendNotification("MoodPunk", "¿Todo bien? 💫 Este es un recordatorio de prueba.");
      toast.success("Notificación de prueba enviada");
    }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-void-paper border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-neon-cyan" />
            <h3 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide">
              Recordatorios
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!isNotificationSupported() ? (
            <div className="text-center py-4">
              <BellOff className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-inter">
                Tu navegador no soporta notificaciones
              </p>
            </div>
          ) : permission === "denied" ? (
            <div className="text-center py-4">
              <BellOff className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-slate-400 font-inter mb-2">
                Las notificaciones están bloqueadas
              </p>
              <p className="text-xs text-slate-500">
                Actívalas en la configuración de tu navegador
              </p>
            </div>
          ) : (
            <>
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-black/30 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  {enabled ? (
                    <Bell className="w-6 h-6 text-neon-green" />
                  ) : (
                    <BellOff className="w-6 h-6 text-slate-500" />
                  )}
                  <div>
                    <p className="font-rajdhani font-semibold text-white">
                      Recordatorios
                    </p>
                    <p className="text-xs text-slate-500">
                      {enabled ? "Activados" : "Desactivados"}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={enabled ? handleDisableNotifications : handleEnableNotifications}
                  disabled={loading}
                  data-testid="toggle-notifications-btn"
                  className={`px-4 py-2 font-rajdhani font-bold uppercase text-sm rounded-lg transition-all ${
                    enabled
                      ? "bg-neon-green/20 text-neon-green border border-neon-green/50"
                      : "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/50"
                  }`}
                >
                  {loading ? "..." : enabled ? "Desactivar" : "Activar"}
                </Button>
              </div>

              {/* Interval Selection */}
              {enabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-neon-purple" />
                    <p className="font-rajdhani font-semibold text-sm text-slate-300 uppercase">
                      Frecuencia de recordatorios
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {INTERVALS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleIntervalChange(opt.value)}
                        data-testid={`interval-${opt.value}`}
                        className={`p-3 rounded-lg text-sm font-rajdhani transition-all ${
                          interval === opt.value
                            ? "bg-neon-purple/20 text-neon-purple border border-neon-purple/50"
                            : "bg-white/5 text-slate-400 border border-white/10 hover:border-white/20"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Test Notification */}
                  <Button
                    onClick={handleTestNotification}
                    data-testid="test-notification-btn"
                    className="w-full py-3 mt-4 font-rajdhani font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-lg transition-all"
                  >
                    Enviar notificación de prueba
                  </Button>
                </motion.div>
              )}

              {/* Info */}
              <div className="bg-neon-cyan/5 border border-neon-cyan/20 rounded-xl p-4">
                <p className="text-sm text-neon-cyan/80 font-inter">
                  <strong>¿Todo bien?</strong> Te enviaremos recordatorios amigables para que registres cómo te sientes.
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// Hook to manage notification reminders
export const useNotificationReminder = () => {
  const [settings, setSettings] = useState({ enabled: false, interval: 60 });

  // Load settings
  useEffect(() => {
    const stored = localStorage.getItem("moodpunk_notifications");
    if (stored) {
      setSettings(JSON.parse(stored));
    }
  }, []);

  // Set up reminder interval
  useEffect(() => {
    if (!settings.enabled || Notification.permission !== "granted") {
      return;
    }

    const intervalMs = settings.interval * 60 * 1000; // Convert minutes to ms
    
    const timer = setInterval(() => {
      const message = getRandomMessage();
      sendNotification("MoodPunk", message, () => {
        // Focus window when notification is clicked
        window.focus();
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [settings.enabled, settings.interval]);

  return settings;
};

// Notification Bell Button Component
export const NotificationBellButton = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("moodpunk_notifications");
    if (stored) {
      const parsed = JSON.parse(stored);
      setEnabled(parsed.enabled && Notification.permission === "granted");
    }
  }, [showSettings]);

  return (
    <div className="relative">
      <button
        onClick={() => setShowSettings(true)}
        data-testid="notification-settings-btn"
        className={`p-2 rounded-lg transition-all ${
          enabled
            ? "bg-neon-green/10 border border-neon-green/30 text-neon-green"
            : "bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-white/20"
        }`}
        title="Configurar recordatorios"
      >
        {enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {showSettings && (
          <NotificationSettings onClose={() => setShowSettings(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};
